import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import NotaLeituraModal from "./NotaLeituraModal";
import EstoquePanel from "./EstoquePanel";
import EquipePanel from "./EquipePanel";
import { listarFotosOffline, removerFotoOffline } from "../lib/offlineFotos";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BRL = (v) => (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const CATEGORIAS = ["Insumos", "Mão de obra", "Encargos", "Frete", "Manutenção", "Protetor de chuva", "Outros"];

export const FinanceiroPanel = ({ showEstoque = false }) => {
  const [aba, setAba] = useState("caixa");
  const [notaModal, setNotaModal] = useState(null);
  const [filaNotas, setFilaNotas] = useState([]);
  const [online, setOnline] = useState(navigator.onLine);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ type: "venda", date: "", kg: "", price: "", amount: "", category: "Insumos", description: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/financeiro/entries?month=${month}`).then((r) => setData(r.data)).catch(() => {});
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const carregarFilaNotas = useCallback(() => {
    listarFotosOffline("nota").then(setFilaNotas).catch(() => {});
  }, []);
  useEffect(() => {
    carregarFilaNotas();
    const on = () => { setOnline(true); carregarFilaNotas(); };
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, [carregarFilaNotas]);

  const vendaTotal = (parseFloat(form.kg) || 0) * (parseFloat(form.price) || 0);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/financeiro/entries`, {
        type: form.type,
        date: form.date || undefined,
        kg: parseFloat(form.kg) || 0,
        price_per_kg: parseFloat(form.price) || 0,
        amount: parseFloat(form.amount) || 0,
        category: form.type === "despesa" ? form.category : "Venda de borracha",
        description: form.description,
      });
      toast.success(form.type === "venda" ? "Venda registrada! 💰" : "Despesa registrada!");
      setForm({ ...form, kg: "", price: "", amount: "", description: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar");
    }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este lançamento?")) return;
    try {
      await axios.delete(`${API}/financeiro/entries/${id}`);
      load();
    } catch { toast.error("Erro ao excluir"); }
  };

  const receber = async (id) => {
    try {
      await axios.patch(`${API}/financeiro/entries/${id}/receber`);
      toast.success("Recebimento confirmado 💰");
      load();
    } catch { toast.error("Erro ao marcar recebido"); }
  };

  const Abas = ({ ativa }) => (
    <div className="flex gap-2">
      {[["caixa", "💰 Caixa"], ["estoque", "📦 Estoque"], ["equipe", "👷 Equipe"]].map(([v, l]) => (
        <button key={v} onClick={() => setAba(v)}
          className={`flex-1 py-2 rounded-lg text-sm font-semibold ${ativa === v ? "bg-green-700 text-white" : "bg-white border text-gray-600"}`}
          data-testid={`financeiro-aba-${v}`}>{l}</button>
      ))}
    </div>
  );

  if (showEstoque && aba === "estoque") {
    return (
      <div className="space-y-3" data-testid="financeiro-panel">
        <Abas ativa="estoque" />
        <EstoquePanel />
      </div>
    );
  }

  if (showEstoque && aba === "equipe") {
    return (
      <div className="space-y-3" data-testid="financeiro-panel">
        <Abas ativa="equipe" />
        <EquipePanel />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="financeiro-panel">
      {notaModal && <NotaLeituraModal
        fotoInicial={notaModal.item?.dataUrl || null}
        offlineId={notaModal.item?.id || null}
        onFilaChange={carregarFilaNotas}
        onClose={() => { setNotaModal(null); carregarFilaNotas(); }}
        onDone={(m) => { setNotaModal(null); carregarFilaNotas(); if (m && m.length === 7 && m !== month) setMonth(m); else load(); }} />}
      {showEstoque && <Abas ativa="caixa" />}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-base">{showEstoque ? "💰 Financeiro do Seringal" : "💰 Meu Caixa"}</CardTitle>
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="border rounded-lg px-2 py-1.5 text-sm"
              data-testid="financeiro-month"
            />
          </div>
          <p className="text-xs text-muted-foreground">Registre vendas de borracha e despesas — o caixa do mês se monta sozinho.</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center" data-testid="financeiro-entradas">
              <p className="text-[11px] text-green-700 font-semibold">ENTRADAS</p>
              <p className="text-[13px] leading-tight sm:text-lg font-bold text-green-800">{BRL(data?.entradas)}</p>
              {data?.kg_vendidos > 0 && <p className="text-[10px] text-green-600">{data.kg_vendidos.toLocaleString("pt-BR")} kg</p>}
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-center" data-testid="financeiro-saidas">
              <p className="text-[11px] text-red-700 font-semibold">SAÍDAS</p>
              <p className="text-[13px] leading-tight sm:text-lg font-bold text-red-700">{BRL(data?.saidas)}</p>
            </div>
            <div className={`${(data?.saldo ?? 0) >= 0 ? "bg-emerald-50 border-emerald-300" : "bg-amber-50 border-amber-300"} border rounded-xl p-3 text-center`} data-testid="financeiro-saldo">
              <p className="text-[11px] text-gray-700 font-semibold">SALDO</p>
              <p className="text-[13px] leading-tight sm:text-lg font-bold text-gray-900">{BRL(data?.saldo)}</p>
            </div>
          </div>
          {data?.a_receber > 0 && (
            <div className="mt-2 bg-blue-50 border border-blue-200 rounded-xl px-3 py-2 flex items-center justify-between" data-testid="financeiro-a-receber">
              <span className="text-[11px] font-semibold text-blue-800">⏳ A RECEBER</span>
              <span className="text-sm font-bold text-blue-900">{BRL(data.a_receber)}</span>
            </div>
          )}

          <Button onClick={() => setNotaModal({})} className="mt-3 w-full bg-blue-700 hover:bg-blue-800 h-auto py-2.5 whitespace-normal" data-testid="financeiro-nota-btn">
            📸 Lançar por foto da nota — IA lê, você confere
          </Button>
          {!online && <p className="text-xs text-amber-700 mt-1 text-center font-semibold" data-testid="financeiro-offline-aviso">📴 Sem conexão — a foto da nota fica salva no aparelho e você lança quando o sinal voltar.</p>}

          {filaNotas.length > 0 && (
            <div className="mt-2 bg-amber-50 border border-amber-300 rounded-xl p-3 space-y-2" data-testid="financeiro-fila-notas">
              <div className="text-xs font-bold text-amber-800">📴 Notas salvas no aparelho ({filaNotas.length}) — fotografadas sem conexão</div>
              {filaNotas.map((n) => (
                <div key={n.id} className="flex items-center gap-2 bg-white border rounded-lg p-2" data-testid={`financeiro-fila-nota-${n.id}`}>
                  <img src={n.dataUrl} alt="Nota offline" className="w-12 h-12 rounded object-cover flex-shrink-0" />
                  <div className="flex-1 text-[10px] text-gray-500">{new Date(n.criadoEm).toLocaleString("pt-BR")}</div>
                  <Button size="sm" disabled={!online} onClick={() => setNotaModal({ item: n })} className="bg-blue-700 hover:bg-blue-800 text-xs h-8" data-testid={`financeiro-fila-lancar-${n.id}`}>📤 Lançar</Button>
                  <button onClick={async () => { await removerFotoOffline(n.id).catch(() => {}); carregarFilaNotas(); }} className="text-red-500 px-1" data-testid={`financeiro-fila-excluir-${n.id}`}>🗑️</button>
                </div>
              ))}
            </div>
          )}

          <form onSubmit={save} className="mt-4 border rounded-xl p-3 bg-gray-50">
            <div className="flex gap-2">
              <button type="button" onClick={() => setForm({ ...form, type: "venda" })} className={`flex-1 py-2 rounded-lg text-sm font-semibold ${form.type === "venda" ? "bg-green-600 text-white" : "bg-white border text-gray-600"}`} data-testid="financeiro-tipo-venda">➕ Venda</button>
              <button type="button" onClick={() => setForm({ ...form, type: "despesa" })} className={`flex-1 py-2 rounded-lg text-sm font-semibold ${form.type === "despesa" ? "bg-red-600 text-white" : "bg-white border text-gray-600"}`} data-testid="financeiro-tipo-despesa">➖ Despesa</button>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-3">
              {form.type === "venda" ? (
                <>
                  <Input type="number" step="any" placeholder="Quantos kg?" value={form.kg} onChange={(e) => setForm({ ...form, kg: e.target.value })} data-testid="financeiro-kg" />
                  <Input type="number" step="any" placeholder="R$ por kg" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="financeiro-preco" />
                </>
              ) : (
                <>
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="border rounded-lg px-2 py-2 text-sm bg-white" data-testid="financeiro-categoria">
                    {CATEGORIAS.map((c) => <option key={c}>{c}</option>)}
                  </select>
                  <Input type="number" step="any" placeholder="Valor (R$)" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="financeiro-valor" />
                </>
              )}
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="financeiro-data" />
              <Input placeholder="Observação (opcional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="financeiro-obs" />
            </div>
            {form.type === "venda" && vendaTotal > 0 && (
              <p className="text-sm font-bold text-green-700 mt-2" data-testid="financeiro-venda-total">Total da venda: {BRL(vendaTotal)}</p>
            )}
            <Button type="submit" disabled={saving} className="mt-3 w-full sm:w-auto bg-green-700 hover:bg-green-800" data-testid="financeiro-salvar">
              {saving ? "Salvando..." : "Salvar lançamento"}
            </Button>
          </form>

          <div className="mt-4 space-y-2" data-testid="financeiro-lista">
            {(data?.entries || []).length === 0 && <p className="text-sm text-muted-foreground text-center py-2">Nenhum lançamento neste mês.</p>}
            {(data?.entries || []).map((e) => (
              <div key={e.id} className="border rounded-lg p-2.5 flex items-center justify-between gap-2" data-testid={`financeiro-item-${e.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium">
                    {e.type === "venda" ? "💰" : "💸"} {e.type === "venda" ? `Venda ${e.kg > 0 ? `· ${e.kg.toLocaleString("pt-BR")} kg × ${BRL(e.price_per_kg)}` : ""}` : e.category}
                    {e.description ? ` — ${e.description}` : ""}
                    {e.origem === "nota_ia" ? " 📸" : ""}
                  </p>
                  <p className="text-[11px] text-gray-500">
                    {new Date(`${e.date}T12:00:00`).toLocaleDateString("pt-BR")}
                    {e.drc_pct ? ` · DRC ${e.drc_pct}%` : ""}
                    {e.status === "a_receber" ? " · ⏳ a receber" : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {e.status === "a_receber" && (
                    <button onClick={() => receber(e.id)} className="text-[10px] px-2 py-1 rounded-full bg-blue-700 text-white font-semibold" data-testid={`financeiro-receber-${e.id}`}>✓ Recebi</button>
                  )}
                  <span className={`text-sm font-bold ${e.type === "venda" ? (e.status === "a_receber" ? "text-blue-600" : "text-green-700") : "text-red-600"}`}>{e.type === "venda" ? "+" : "-"}{BRL(e.amount)}</span>
                  <button onClick={() => remove(e.id)} className="text-gray-400 hover:text-red-600 text-sm" data-testid={`financeiro-del-${e.id}`}>🗑</button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
